/**
 * GWP market gate — is a Kite free-gift campaign offered in the CURRENT market?
 *
 * WHY THIS EXISTS
 * Kite loads `window.gfg.settings` in full on EVERY market, including markets the
 * merchant has excluded from the campaign. It simply declines to RENDER there.
 * Our own GWP surfaces (the threshold bar's gift tier, and the quick-buy sheet's
 * picker) read that same settings object, so without a gate they advertise — and
 * offer to claim — a gift the customer cannot have. On Ireland that meant the bar
 * saying "You've unlocked every reward" and four clickable gift rows that did
 * nothing, because Kite's claim fn refuses and the discount Function never fires.
 *
 * THE SIGNAL (verified live, 2026-08-11)
 * The merchant's market rule is declared in Kite's own campaign config:
 *   gfg.settings.freeGifts[i].conditionsData
 *     = { conditionsOperator: 'AND',
 *         conditionsList: [{ ruleType: 'markets',
 *                            ruleValue: { operatorType: 'isNot', value: ['international'] } }] }
 * and the live market handle is injected by Kite's own app-embed Liquid at
 *   window.shopifyLiquidValuesApp7Ext.markets.handle   // 'international' on Ireland
 * We evaluate the merchant's rule rather than hardcoding a market, so changing the
 * exclusion in Kite's admin is reflected on the storefront with no theme edit.
 *
 * Deliberately NOT used as signals (both verified identical across markets):
 *   • discountData_shopifyResponse.status — 'ACTIVE' on excluded markets too
 *   • merchantInfo.currency / countryCode — hardcoded 'GBP' / 'GB'
 *
 * Loaded from layout/theme.liquid ahead of every GWP consumer. Deferred scripts
 * execute in document order, so the <head> include here is defined before
 * quick-buy-gwp.js and before the cart drawer's threshold-bar.js.
 */
(function () {
  'use strict';
  if (window.NotedGwpMarket) return;

  // Kite's app embed writes the live market here. Fall back to Kite's own state.
  function marketHandle() {
    var v = window.shopifyLiquidValuesApp7Ext;
    if (v && v.markets && v.markets.handle) return String(v.markets.handle);
    var g = window.gfg;
    var m = g && g.state && g.state.activeCustomerData && g.state.activeCustomerData.markets;
    return m && m.handle ? String(m.handle) : null;
  }

  function visitingCountry() {
    var g = window.gfg;
    var c = g && g.state && g.state.activeCustomerData && g.state.activeCustomerData.visitingFromCountry;
    if (c) return String(c);
    return (window.Shopify && window.Shopify.country) ? String(window.Shopify.country) : null;
  }

  function listHas(vals, needle) {
    if (!Array.isArray(vals) || needle == null) return false;
    for (var i = 0; i < vals.length; i++) {
      if (String(vals[i]).toLowerCase() === String(needle).toLowerCase()) return true;
    }
    return false;
  }

  // Returns true/false for rules we understand, null for ones we don't (so an
  // unknown rule type never silently suppresses a campaign we should be showing).
  function evalRule(rule) {
    if (!rule) return null;
    var type = rule.ruleType;
    var rv = rule.ruleValue || {};
    var op = rv.operatorType;
    var vals = rv.value;
    var actual;

    if (type === 'markets' || type === 'market') actual = marketHandle();
    else if (type === 'countrySelects') actual = visitingCountry();
    else return null;

    // Fail CLOSED when we can't identify where the shopper is — same posture as
    // Kite's own evaluator (`if (!currentMarketHandle) return false`). Better to
    // hide a gift we can't confirm than to promise one that won't be honoured.
    if (!actual) return false;

    if (op === 'is') return listHas(vals, actual);
    if (op === 'isNot') return !listHas(vals, actual);
    return null;
  }

  /**
   * Is this campaign offered in the current market?
   * @param {object} campaign - one entry of gfg.settings.freeGifts
   * @param {number} [index]  - its index, used to prefer Kite's own evaluator
   * @returns {boolean}
   */
  // Free gift test (Theme settings > CRO tests > Free gift test, 29 Oct 2026):
  // the no-gift group is offered no campaign at all. Every gift surface the
  // theme draws reads through this gate (the progress bar's gift step, the
  // phone sheet's picker, the claim layer), so one check here takes the gift
  // away everywhere the theme controls it; Kite's own widgets are hidden by
  // the group's CSS. Only 'none' blocks: 'control' (and a later 'preselected'
  // group for the picked vs preselected test) keep the gift.
  function noGiftGroup() {
    try { return typeof window.notedGiftGroup === 'function' && window.notedGiftGroup() === 'none'; }
    catch (e) { return false; }
  }

  function allows(campaign, index) {
    if (!campaign) return false;
    if (noGiftGroup()) return false;

    // Prefer Kite's own condition evaluator when it's booted: it covers every
    // condition type (markets, customer tags, schedule, URL) with no drift.
    try {
      var f = window.gfg && window.gfg.gfgFreeGift && window.gfg.gfgFreeGift.f;
      var check = f && f.gfgFreeGiftCheckCustomerConditions;
      if (typeof check === 'function' && typeof index === 'number') {
        var verdict = check(index);
        if (typeof verdict === 'boolean') return verdict;
      }
    } catch (e) {}

    // Fall back to evaluating the merchant's own rule list ourselves.
    var data = campaign.conditionsData;
    var list = data && data.conditionsList;
    if (!Array.isArray(list) || !list.length) return true; // no restriction configured

    var results = [];
    for (var i = 0; i < list.length; i++) {
      var r = evalRule(list[i]);
      if (r !== null) results.push(r);
    }
    if (!results.length) return true; // nothing we understand — don't suppress

    if (data.conditionsOperator === 'OR') {
      return results.indexOf(true) !== -1;
    }
    for (var j = 0; j < results.length; j++) if (!results[j]) return false;
    return true;
  }

  /**
   * Kite V2 ("FREE_GIFT_V2") campaigns live in gfg.settings.freeGiftsV2 and
   * declare their market rule in a different place:
   *   customerEligibilityRules = { rulesOperator: 'OR',
   *     rulesList: [{ ruleType: 'market', ruleValue: { operatorType: 'is', value: [<market handle>] } }] }
   * (verified live 28 Sep 2026). Same evaluator, same fail-closed posture.
   * A disabled or test-mode campaign is never offered.
   */
  function allowsV2(campaign) {
    // Kite skips anything not explicitly enabled (`!isEnabled`).
    if (!campaign || !campaign.isEnabled) return false;
    if (noGiftGroup()) return false;

    // Kite's own schedule and campaign-link checks when its V2 module is up,
    // so the bar never promises a gift Kite is hiding (a scheduled campaign
    // past its end date, or one only switched on by a campaign link / test
    // mode URL). Kite's full eligibility.check() is deliberately NOT used: it
    // also gates on page type, which would drop the gift from the basket bar
    // on collection pages where the side-cart widget does show.
    try {
      var el = window.gfg && window.gfg.gfgFreeGiftV2 && window.gfg.gfgFreeGiftV2.eligibility;
      if (el && typeof el._checkSchedule === 'function' && !el._checkSchedule(campaign.campaignScheduleData)) return false;
      if (el && typeof el._checkCampaignUrl === 'function') {
        if (!el._checkCampaignUrl(campaign)) return false;
      } else if (campaign.isTestMode || campaign.isCampaignUrlEnabled) {
        return false;                                   // can't verify the link → fail closed
      }
    } catch (e) {}

    var data = campaign.customerEligibilityRules;
    var list = data && data.rulesList;
    if (!Array.isArray(list) || !list.length) return true;

    var results = [];
    for (var i = 0; i < list.length; i++) {
      var r = evalRule(list[i]);
      if (r !== null) results.push(r);
    }
    if (!results.length) return true;
    if (data.rulesOperator === 'OR') return results.indexOf(true) !== -1;
    for (var j = 0; j < results.length; j++) if (!results[j]) return false;
    return true;
  }

  window.NotedGwpMarket = {
    allows: allows,
    allowsV2: allowsV2,
    marketHandle: marketHandle,
    noGiftGroup: noGiftGroup,
    // Cache-busting suffix so a tier cached on one market (or for the gift
    // test's no-gift group) can't replay on another.
    key: function () { return (marketHandle() || 'unknown') + (noGiftGroup() ? ':nogift' : ''); }
  };
})();
